<template>
  <AuthLayout :mode="mode">
    <Section heading="Manufacturers we Deal with" :mode="mode" />
    <template
      v-if="contactData && contactData.mfrs && contactData.mfrs.length > 0"
    >
      <div class="mb-6 sm:mb-4">
        <AppButton title="Update" @action="updateMfrs" />
      </div>
      <AppTable :mode="mode" :columns="columns" :isHidden="false">
        <template #rows>
          <tr class="select-all-row">
            <td>
              <AppCheckbox
                :mode="mode"
                id="select-all-is-rep"
                v-model="allIsRep"
                title="Select all"
                classes="checkbox-long"
              />
            </td>
            <td>
              <div class="flex justify-center">
                <AppCheckbox
                  :mode="mode"
                  id="select-all-buy"
                  v-model="allInstantBuy"
                />
              </div>
            </td>
            <td>
              <div class="flex justify-center">
                <AppCheckbox
                  :mode="mode"
                  id="select-all-sell"
                  v-model="allInstantSell"
                />
              </div>
            </td>
            <td>
              <div class="flex justify-center">
                <AppCheckbox
                  :mode="mode"
                  id="select-all-summary"
                  v-model="allRecSummary"
                />
              </div>
            </td>
          </tr>
          <tr v-for="(mfr, key) in contactData.mfrs" :key="key">
            <td>
              <AppCheckbox
                :mode="mode"
                :id="mfr.mfr_id"
                v-model="mfr.is_rep"
                :title="mfr.company ? mfr.company : '-'"
                classes="checkbox-long"
              />
            </td>
            <td>
              <div class="flex justify-center">
                <AppCheckbox
                  :mode="mode"
                  :id="mfr.mfr_id + '-' + 'buy'"
                  v-model="mfr.instant_buy"
                />
              </div>
            </td>
            <td>
              <div class="flex justify-center">
                <AppCheckbox
                  :mode="mode"
                  :id="mfr.mfr_id + '-' + 'sell'"
                  v-model="mfr.instant_sell"
                />
              </div>
            </td>
            <td>
              <div class="flex justify-center">
                <AppCheckbox
                  :mode="mode"
                  :id="mfr.mfr_id + '-' + 'summary-email'"
                  v-model="mfr.rec_summary_email"
                />
              </div>
            </td>
          </tr>
        </template>
      </AppTable>
      <div class="mt-6 sm:mt-4">
        <AppButton title="Update" @action="updateMfrs" />
      </div>
    </template>
    <EmptyData
      v-else
      :notFound="'No manufacturers represented!'"
      :mode="mode"
    />
  </AuthLayout>
</template>

<script>
import Section from "@C/Common/Section.vue";
import ApiRequest from "@L/ApiRequest";
import { useGlobalErrorHandler } from "@L/errorHandler";
import { useUserStore } from "@S/user";
import { useToastStore } from "@S/toast";
import { useLoaderStore } from "@S/loader";
import EmptyData from "@C/Common/EmptyData.vue";
import AuthLayout from "@A/AuthLayout.vue";
import AppTable from "@C/Tables/AppTable.vue";
import AppCheckbox from "@C/Form/AppCheckbox.vue";
import AppButton from "@C/Common/AppButton.vue";

export default {
  name: "ContactManufacturers",
  components: {
    AuthLayout,
    Section,
    EmptyData,
    AppTable,
    AppCheckbox,
    AppButton,
  },
  setup() {
    const userStore = useUserStore();
    const toastStore = useToastStore();
    const loaderStore = useLoaderStore();
    const { handleError } = useGlobalErrorHandler();
    return {
      userStore,
      toastStore,
      loaderStore,
      handleError,
    };
  },
  props: {
    mode: String,
  },
  data() {
    return {
      id: "",
      contactData: null,
      memberData: null,
      columns: [
        {
          name: "Manufacturers",
        },
        {
          name: "Buy",
          classes: "w-[50px]",
        },
        {
          name: "Sell",
          classes: "w-[50px]",
        },
        {
          name: "Summary Email",
          classes: "w-[150px]",
        },
      ],
    };
  },
  created() {
    this.id = this.$route.params.id;
  },
  mounted() {
    this.getContactManufacturers();
  },
  computed: {
    mfrs() {
      return (this.contactData && this.contactData.mfrs) || [];
    },
    allIsRep: {
      get() {
        return this.mfrs.length > 0 && this.mfrs.every((m) => m.is_rep);
      },
      set(v) {
        this.mfrs.forEach((m) => (m.is_rep = v));
      },
    },
    allInstantBuy: {
      get() {
        return this.mfrs.length > 0 && this.mfrs.every((m) => m.instant_buy);
      },
      set(v) {
        this.mfrs.forEach((m) => (m.instant_buy = v));
      },
    },
    allInstantSell: {
      get() {
        return this.mfrs.length > 0 && this.mfrs.every((m) => m.instant_sell);
      },
      set(v) {
        this.mfrs.forEach((m) => (m.instant_sell = v));
      },
    },
    allRecSummary: {
      get() {
        return this.mfrs.length > 0 && this.mfrs.every((m) => m.rec_summary_email);
      },
      set(v) {
        this.mfrs.forEach((m) => (m.rec_summary_email = v));
      },
    },
  },
  methods: {
    async getContactManufacturers() {
      this.loaderStore.setIsLoading(true);
      try {
        const { data } = await ApiRequest().get(`/contact/${this.id}`);
        if (data && data.contact) {
          this.contactData = data.contact;
          this.memberData = data.member;
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
    async updateMfrs() {
      this.loaderStore.setIsLoading(true);
      const payload = {
        contact: this.contactData,
        member: this.memberData,
      };
      try {
        const { data } = await ApiRequest().post(`/contact`, payload);
        if (data) {
          this.toastStore.show(
            `Manufacturers updated successfully!`,
            "success",
          );
          this.getContactManufacturers();
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
  },
};
</script>
